import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ConfirmDialog from '@dashboard/components/ConfirmDialog';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { chargeGuestsKey, chargeKey, chargeTicketsKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';

export const reviewPrompt = (row, reviewed = true) => {
  const whose = row?.name ? `${row.name}'s` : 'these';
  return reviewed
    ? { title: `Mark ${whose} details as reviewed?`, confirmLabel: 'Mark as reviewed' }
    : { title: `Undo the review of ${whose} details?`, confirmLabel: 'Undo review' };
};

export default function useBookingGuestActions(bookingId) {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();
  // Closing keeps the row so the dialog's text holds while it fades out.
  const [pendingReview, setPendingReview] = useState({ open: false, row: null, reviewed: true });
  const base = `/sellers/${sellerId}/charges/${bookingId}/guests`;
  // A refetch, not the save's reply: the merchant view adds each row's ticket.
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: chargeGuestsKey(bookingId) });
    queryClient.invalidateQueries({ queryKey: chargeKey(bookingId) });
    queryClient.invalidateQueries({ queryKey: chargeTicketsKey(bookingId) });
    queryClient.invalidateQueries({ queryKey: ['attendees'] });
  };

  const saveGuest = async (row, input) => {
    const { warnings } = await fetcher(`${base}/${row.position}`, token, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    refresh();
    if (warnings.length)
      toast({
        title: 'Attendee saved',
        description: warnings.join(' '),
        type: 'alert',
        duration: 6000,
      });
    else toast({ title: 'Attendee saved' });
  };

  const { mutate: reviewGuest, isPending: isReviewingGuest } = useMutation({
    mutationFn: ({ row, reviewed }) =>
      fetcher(`${base}/${row.position}/review`, token, {
        method: 'POST',
        body: JSON.stringify({ reviewed }),
      }),
    onSuccess: refresh,
    onError: error =>
      toast({ title: "Couldn't update the review", description: error?.message, type: 'error' }),
  });

  const askToReview = (row, reviewed) => setPendingReview({ open: true, row, reviewed });

  const reviewDialog = (
    <ConfirmDialog
      open={pendingReview.open}
      onOpenChange={open => setPendingReview(current => ({ ...current, open }))}
      {...reviewPrompt(pendingReview.row, pendingReview.reviewed)}
      onConfirm={() => reviewGuest({ row: pendingReview.row, reviewed: pendingReview.reviewed })}
    />
  );

  return { saveGuest, askToReview, reviewDialog, isReviewingGuest };
}
